type Entry = { data: any[]; ts: number };

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<any[] | { [phone: string]: any[] }>>();

export function invalidateHistory(phone?: string) {
  if (phone) {
    const keysToDelete = [];
    for (const key of cache.keys()) {
      if (key.startsWith(phone)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      cache.delete(key);
      inflight.delete(key);
    });
  } else {
    cache.clear();
    inflight.clear();
  }
}

export async function getMessageHistoryOnce(params: {
  phone: string;
  session: { token_type: string; access_token: string } | null;
  ttlMs?: number;
  from?: string;
  to?: string;
  userId?: string | number; 
}) {
  const { phone, session, ttlMs = 60_000, from, to, userId } = params;
  if (!session) throw new Error("No session");

  const cacheKey = `${phone}-${from || 'no-from'}-${to || 'no-to'}-${userId || 'no-user'}`;
  
  cache.delete(cacheKey);
  inflight.delete(cacheKey);
  
  const now = Date.now();
  const cached = cache.get(cacheKey);
  if (cached && now - cached.ts < ttlMs) {
    return cached.data;
  }

  const pending = inflight.get(cacheKey);
  if (pending) {
    return pending;
  }

  const p = (async () => {
    const encodedTo = encodeURIComponent(phone);
    let url = `${process.env.REACT_APP_BACKEND_URL}/api/twilio/message-history?to=${encodedTo}`;
    
    if (from) {
      url += `&from=${encodeURIComponent(from)}`;
    }
    if (to) {
      url += `&toDate=${encodeURIComponent(to)}`;
    }
    if (userId) {
      url += `&userId=${encodeURIComponent(userId)}`;
    }

    const res = await fetch(url, {
      method: "GET", 
      headers: { Authorization: `${session.token_type} ${session.access_token}` }
    });
    if (!res.ok) throw new Error(res.statusText);
    const data = await res.json();

    const processedData = data.map((msg: any) => {
      let messageType: "text" | "image" | "audio" | "assistant" = "text";
      
      if (msg.messageType === "audio") {
        messageType = "audio";
      } else if (msg.messageType === "image") {
        messageType = "image";
      } else if (msg.messageType === "assistant") {
        messageType = "assistant";
      } else {
        const bodyLower = (msg.body || '').toLowerCase();
        const fileNameLower = (msg.fileName || '').toLowerCase();
        
        if (bodyLower.includes('🎵') || 
            bodyLower.includes('audio') || 
            fileNameLower.endsWith('.mp3') ||
            fileNameLower.endsWith('.ogg') ||
            fileNameLower.endsWith('.webm') ||
            fileNameLower.endsWith('.wav') ||
            fileNameLower.endsWith('.m4a') ||
            fileNameLower.endsWith('.aac')) {
          messageType = "audio";
        } else if (bodyLower.includes('📷') || 
                 bodyLower.includes('imagen') ||
                 bodyLower.includes('image') ||
                 fileNameLower.endsWith('.jpg') ||
                 fileNameLower.endsWith('.jpeg') ||
                 fileNameLower.endsWith('.png') ||
                 fileNameLower.endsWith('.gif') ||
                 fileNameLower.endsWith('.webp')) {
          messageType = "image";
        }
      }

      return {
        ...msg,
        sid: msg.message_sid || msg.sid || null,
        isReply: msg.is_reply || false,
        originalMessageSid: msg.original_message_sid || null,
        replyToBody: msg.reply_to_body || null,
        messageType: messageType,
        mediaUrl: msg.imageUrl,  
        media_url: msg.imageUrl,
        mediaType: msg.messageType,
        media_type: msg.messageType,
        fileName: msg.fileName,
        file_name: msg.fileName,
        fileSize: msg.fileSize,
        file_size: msg.fileSize,
        imageUrl: msg.imageUrl, 
      };
    });
    
    cache.set(cacheKey, { data: processedData, ts: Date.now() });
    inflight.delete(cacheKey);
    return processedData;
  })().catch((e) => {
    inflight.delete(cacheKey);
    throw e;
  });

  inflight.set(cacheKey, p);
  return p;
}

export async function getBulkMessageHistory(params: {
  session: { token_type: string; access_token: string } | null;
  from?: string;
  to?: string;
}) {
  const { session, from, to } = params;
  if (!session) throw new Error("No session");

  const cacheKey = `bulk-all-${from || 'no-from'}-${to || 'no-to'}`;
  
  cache.delete(cacheKey);
  inflight.delete(cacheKey);
  
  const pending = inflight.get(cacheKey);
  if (pending) {
    return pending;
  }

  const p = (async () => {
    let url = `${process.env.REACT_APP_BACKEND_URL}/api/twilio/message-history-bulk`;
    
    const params = new URLSearchParams();
    if (from) {
      params.append('from', from);
    }
    if (to) {
      params.append('toDate', to);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const res = await fetch(url, {
      method: "GET", 
      headers: { Authorization: `${session.token_type} ${session.access_token}` }
    });
    
    if (!res.ok) throw new Error(res.statusText);
    const data = await res.json();

    // El backend ahora devuelve un array de usuarios con historial
    // Formato: [{ id, phone, name, company_id }]
    
    inflight.delete(cacheKey);
    return data;
  })().catch((e) => {
    inflight.delete(cacheKey);
    throw e;
  });

  inflight.set(cacheKey, p);
  return p;
}

